import {
  Box, Button, Checkbox, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, Grid, MenuItem, TextField, Typography, Divider, Alert,
  Radio, RadioGroup, IconButton, Tooltip, SelectChangeEvent,
  Table, TableHead, TableBody, TableRow, TableCell
} from '@mui/material';
import React, { FC, useEffect, useState, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import CustomSelect, { IOptions } from '../FormFields/CustomSelect';
import CustomDataGrid from '../DataGrid/DataGrid';
import { GridValueGetterParams, GridColDef } from "@mui/x-data-grid";
import { Utils } from '../../utils/common-utils';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";
import LoadingButton from '@mui/lab/LoadingButton';
import { ApiStatus } from "../../utils/api.types";
import { fetchEmployees } from '../../store/employeeStore/employeeThunk';
import { calculateSalary, processSalary } from '../../store/salaryStore/salaryThunk';
import { fetchExpensePayments } from '../../store/expenseStore/expenseThunk';
import { fetchTaxRates } from '../../store/gstStore/gstThunk';
import { fetchBanks } from '../../store/bankStore/bankThunk';
import { fetchCostCentres } from '../../store/costCentreStore/costCentreThunk';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

interface DialogProcessSalaryFormProps {
  title: String;
  open: boolean;
  onClose: (refresh: any) => void;
}

interface ITdsOption extends IOptions {
  rate?: number;
  taxSection?: string;
  taxName?: string;
}

const DialogProcessSalaryForm: FC<DialogProcessSalaryFormProps> = ({ onClose, open, title }) => {

  const { status, salaryCalculation } = useAppSelector(state => state.salaries);
  const { selectedAccount } = useAppSelector(state => state.auth);
  const { employees } = useAppSelector(state => state.employees);
  const { costCentres } = useAppSelector(state => state.costCentres);
  const dispatch = useAppDispatch();

  // Form states
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [workingDays, setWorkingDays] = useState(26);
  const [presentDays, setPresentDays] = useState(26);

  // Salary component states
  const [basicSalary, setBasicSalary] = useState<string>('');
  const [hra, setHra] = useState<string>('');

  // Additional earnings states (dynamic fields only)
  const [additionalEarnings, setAdditionalEarnings] = useState<Array<{label: string, amount: string}>>([]);

  // Expense add-ons (approved expenses for selected employee)
  const [approvedExpenses, setApprovedExpenses] = useState<any[]>([]);
  const [selectedExpenseIds, setSelectedExpenseIds] = useState<Set<string>>(new Set());
  const [showExpenseAddons, setShowExpenseAddons] = useState(false);

  // Bank account states
  const [selectedFromBankAccount, setSelectedFromBankAccount] = useState('');
  const [fromBankAccountOptions, setFromBankAccountOptions] = useState<IOptions[]>([]);

  // Cost centre state
  const [selectedCostCentre, setSelectedCostCentre] = useState('');

  // Payment mode states
  const [paymentMode, setPaymentMode] = useState('CASH');
  const [paymentStatus, setPaymentStatus] = useState('PROCESSED');
  const [transactionRef, setTransactionRef] = useState('');

  // Tax configuration states
  const [applyTax, setApplyTax] = useState(true);
  const [applyPF, setApplyPF] = useState(true);
  const [applyESI, setApplyESI] = useState(true);
  const [applyPT, setApplyPT] = useState(true);
  const [applyTDS, setApplyTDS] = useState(false);

  // TDS states
  const [tdsTaxRateOptions, setTdsTaxRateOptions] = useState<ITdsOption[]>([]);
  const [tdsSelected, setTdsSelected] = useState({
    _id: "",
    label: "",
    rate: 0
  });

  // Form validation schema
  const schema = yup.object().shape({
    employeeId: yup.string().required("Employee is required."),
    month: yup.string().required("Salary month is required."),
  });

  const methods = useForm<any>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(schema)
  });

  // Load TDS rates once when form opens
  useEffect(() => {
    if (!open) return;

    let isMounted = true;

    const getTdsRates = async () => {
      try {
        const taxRatesData: any = await dispatch(fetchTaxRates()).unwrap();
        if (taxRatesData && taxRatesData.length > 0 && isMounted) {
          const tdsRates = taxRatesData.filter((item: any) => item.taxType === "tds");
          const tdsOptions = tdsRates.map((item: any) => ({
            label: `${item.taxRate}% - ${item.taxSection} ${item.taxName}`,
            value: item._id,
            rate: item.taxRate,
            taxSection: item.taxSection,
            taxName: item.taxName
          }));
          setTdsTaxRateOptions(tdsOptions);
        }
      } catch (error) {
        console.error('Error fetching TDS rates:', error);
      }
    };

    getTdsRates();

    return () => {
      isMounted = false;
    };
  }, [dispatch, open]);

  // Load employees, banks, and cost centres when form opens
  useEffect(() => {
    if (!open || !selectedAccount?._id) return;

    let isMounted = true;

    const loadData = async () => {
      try {
        // Load employees
        await dispatch(fetchEmployees({
          accountId: selectedAccount._id,
          type: 'list',
          params: ''
        }));

        // Load bank accounts
        const bankData: any = await dispatch(fetchBanks(selectedAccount._id));
        if (bankData.payload?.banks?.length > 0 && isMounted) {
          const bankOptions = bankData.payload.banks.map((c: any) => ({
            label: c.accountName + '-' + c.bankName,
            value: c._id
          }));
          setFromBankAccountOptions(bankOptions);

          const firstBank = bankData.payload.banks[0];
          methods.setValue('fromBankAccount', firstBank._id);
          setSelectedFromBankAccount(firstBank._id);
        }

        // Load cost centres (only those enabled for payroll)
        await dispatch(fetchCostCentres({ activeOnly: true, useForPayroll: true }));
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, [selectedAccount?._id, open, dispatch]);

  // Calculate expense add-on total
  const expenseAddOnTotal = useMemo(() => {
    return approvedExpenses
      .filter(e => selectedExpenseIds.has(e._id))
      .reduce((sum, e) => sum + (e.amount || 0), 0);
  }, [approvedExpenses, selectedExpenseIds]);

  // Calculate gross salary automatically
  const calculateGrossSalary = () => {
    const basic = parseFloat(basicSalary) || 0;
    const hraAmount = parseFloat(hra) || 0;
    const additionalTotal = additionalEarnings.reduce((sum, earning) => {
      return sum + (parseFloat(earning.amount) || 0);
    }, 0);
    return basic + hraAmount + additionalTotal;
  };

  // Watch for employee selection changes
  useEffect(() => {
    const subscription = methods.watch((value, { name }) => {
      if (name === 'employeeId' && value.employeeId) {
        const selectedEmployee = employees.find((emp: any) => emp._id === value.employeeId);
        if (selectedEmployee) {
          // Try to get salary from employee data
          const empData: any = selectedEmployee.employee || selectedEmployee;
          const empSalary = empData.grossSalary || empData.ctc || empData.salary?.grossSalary || 0;
          if (empSalary) {
            // Split as Basic 60% and HRA 40% for initial values
            setBasicSalary((empSalary * 0.6).toFixed(2));
            setHra((empSalary * 0.4).toFixed(2));
          }

          // Auto-populate cost centre from employee's default
          if (empData.costCentre) {
            setSelectedCostCentre(empData.costCentre);
          }

          // Fetch approved expenses for this employee
          const loadApprovedExpenses = async () => {
            try {
              const result: any = await dispatch(fetchExpensePayments({
                employee: value.employeeId,
                paymentStatus: 'APPROVED'
              })).unwrap();
              setApprovedExpenses(result?.payments || []);
              setSelectedExpenseIds(new Set());
            } catch (err) {
              setApprovedExpenses([]);
            }
          };
          loadApprovedExpenses();
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [methods, employees, dispatch]);

  // Auto-change payment status to PAID when CASH is selected
  useEffect(() => {
    if (paymentMode === 'CASH' && paymentStatus === 'PROCESSED') {
      setPaymentStatus('PAID');
    }
  }, [paymentMode, paymentStatus]);

  // Memoize TDS options
  const tdsOptionsWithNoTds = useMemo(() => {
    return [{ label: 'Standard Slabs', value: '', rate: 0 }, ...tdsTaxRateOptions];
  }, [tdsTaxRateOptions]);

  // Handle TDS selection change
  const handleTdsChange = (event: any) => {
    const selectedTdsId = event.target.value;

    if (!selectedTdsId) {
      setTdsSelected({ _id: "", label: "Standard Slabs", rate: 0 });
      return;
    }

    const selectedTdsOption = tdsTaxRateOptions.find((opt: ITdsOption) => opt.value === selectedTdsId);
    if (selectedTdsOption) {
      setTdsSelected({
        _id: selectedTdsOption.value,
        label: selectedTdsOption.label,
        rate: selectedTdsOption.rate || 0
      });
    }
  };

  // Handle adding additional earning field
  const handleAddAdditionalEarning = () => {
    setAdditionalEarnings([...additionalEarnings, { label: '', amount: '' }]);
  };

  // Handle removing additional earning field
  const handleRemoveAdditionalEarning = (index: number) => {
    const updated = additionalEarnings.filter((_, i) => i !== index);
    setAdditionalEarnings(updated);
  };

  // Handle additional earning change
  const handleAdditionalEarningChange = (index: number, field: 'label' | 'amount', value: string) => {
    const updated = [...additionalEarnings];
    updated[index][field] = value;
    setAdditionalEarnings(updated);
  };

  // Handle calculate button
  const handleCalculate = async () => {
    const employeeId = methods.getValues('employeeId');
    if (!employeeId) {
      alert('Please select an employee');
      return;
    }

    if (!basicSalary || parseFloat(basicSalary) <= 0) {
      alert('Please enter a valid basic salary');
      return;
    }

    const taxConfig = {
      applyTax,
      applyPF,
      applyESI,
      applyPT,
      tdsConfig: {
        apply: applyTDS,
        rate: tdsSelected.rate,
        label: tdsSelected.label
      }
    };

    const additionalEarningsData = additionalEarnings
      .filter(e => e.label && e.amount)
      .map(e => ({ label: e.label, amount: parseFloat(e.amount) }));

    try {
      await dispatch(calculateSalary({
        employeeId,
        month,
        workingDays,
        presentDays,
        basicSalary: parseFloat(basicSalary),
        hra: parseFloat(hra) || 0,
        taxConfig,
        additionalEarnings: additionalEarningsData
      })).unwrap();
    } catch (error) {
      console.error('Calculate error:', error);
    }
  };

  // Handle form submit
  const onSubmit = async (data: any) => {
    try {
      setFormSubmitted(true);

      const taxConfig = {
        applyTax,
        applyPF,
        applyESI,
        applyPT,
        tdsConfig: {
          apply: applyTDS,
          rate: tdsSelected.rate,
          label: tdsSelected.label
        }
      };

      const paymentOptions = {
        paymentStatus: paymentStatus as 'PROCESSED' | 'PAID',
        paymentMode,
        fromBankAccount: selectedFromBankAccount,
        instrument: transactionRef,
        remarks: data.remarks
      };

      const additionalEarningsData = additionalEarnings
        .filter(e => e.label && e.amount)
        .map(e => ({ label: e.label, amount: parseFloat(e.amount) }));

      await dispatch(processSalary({
        employeeId: data.employeeId,
        month,
        workingDays,
        presentDays,
        basicSalary: parseFloat(basicSalary),
        hra: parseFloat(hra) || 0,
        costCentre: selectedCostCentre || undefined,
        taxConfig,
        paymentOptions,
        additionalEarnings: additionalEarningsData,
        expenseIds: Array.from(selectedExpenseIds)
      })).unwrap();

      methods.reset();
      setFormSubmitted(false);
      onClose(true);
    } catch (error: any) {
      setFormSubmitted(false);
      console.error('Error processing salary:', error);
    }
  };

  const handleClose = () => {
    methods.reset();
    setFormSubmitted(false);
    onClose(false);
  };

  return (
    <div className="drawer-form-container" style={{ width: "100%", maxWidth: "800px" }}>
      <FormProvider {...methods}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100vh",
            padding: "2px 0 0 2px",
            position: "relative"
          }}
          component="form"
          noValidate
        >
          <DialogTitle sx={{ py: 1.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.05rem' }}>
                Process Salary Payment
              </Typography>
            </Box>
          </DialogTitle>

          <DialogContent sx={{
            padding: '16px 24px',
            paddingBottom: '90px',
            overflowY: 'auto',
            flex: 1
          }}>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              {/* Employee Selection and Month in one row */}
              <Grid item xs={12} md={8}>
                <Controller
                  name="employeeId"
                  control={methods.control}
                  render={({ field }) => (
                    <CustomSelect
                      {...field}
                      id="employeeId"
                      label="Select Employee"
                      size="small"
                      options={employees.map((emp: any) => ({
                        label: `${emp.name} (${emp.empId})`,
                        value: emp._id
                      }))}
                      error={Boolean(methods.formState.errors.employeeId)}
                      helperText={methods.formState.errors.employeeId?.message as string}
                    />
                  )}
                />
              </Grid>

              {/* Month Selection */}
              <Grid item xs={12} md={4}>
                <Controller
                  name="month"
                  control={methods.control}
                  defaultValue={month}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      type="month"
                      label="Salary Month"
                      onChange={(e) => {
                        field.onChange(e.target.value);
                        setMonth(e.target.value);
                      }}
                      InputLabelProps={{ shrink: true }}
                      size="small"
                      error={Boolean(methods.formState.errors.month)}
                      helperText={methods.formState.errors.month?.message as string}
                    />
                  )}
                />
              </Grid>

              {/* Cost Centre Selection - Only show if cost centres exist */}
              {costCentres && costCentres.length > 0 && (
                <Grid item xs={12}>
                  <CustomSelect
                    name="costCentre"
                    id="costCentre"
                    label="Cost Centre (Optional)"
                    size="small"
                    fullWidth
                    options={costCentres.map((cc: any) => ({
                      label: cc.alias ? `${cc.name} (${cc.alias})` : cc.name,
                      value: cc._id
                    }))}
                    value={selectedCostCentre}
                    onChange={(e: any) => setSelectedCostCentre(e.target.value)}
                  />
                </Grid>
              )}

              {/* Working Days */}
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Working Days"
                  value={workingDays}
                  onChange={(e) => setWorkingDays(parseInt(e.target.value))}
                  size="small"
                />
              </Grid>

              {/* Present Days */}
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Present Days"
                  value={presentDays}
                  onChange={(e) => setPresentDays(parseInt(e.target.value))}
                  size="small"
                />
              </Grid>

              {/* Basic Salary */}
              <Grid item xs={12} md={6} sx={{ mt: 1 }}>
                <TextField
                  fullWidth
                  type="number"
                  label="Basic Salary"
                  value={basicSalary}
                  onChange={(e) => setBasicSalary(e.target.value)}
                  size="small"
                  required
                  InputProps={{
                    startAdornment: <Typography sx={{ mr: 1 }}>₹</Typography>
                  }}
                />
              </Grid>

              {/* HRA */}
              <Grid item xs={12} md={6} sx={{ mt: 1 }}>
                <TextField
                  fullWidth
                  type="number"
                  label="HRA (House Rent Allowance)"
                  value={hra}
                  onChange={(e) => setHra(e.target.value)}
                  size="small"
                  InputProps={{
                    startAdornment: <Typography sx={{ mr: 1 }}>₹</Typography>
                  }}
                  // helperText="Leave empty if not applicable"
                />
              </Grid>

              {/* Additional Earnings Section */}
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600, color: '#1976d2' }}>
                  Additional Earnings (Optional)
                </Typography>
              </Grid>

              {/* Dynamic Additional Earnings Fields */}
              {additionalEarnings.map((earning, index) => (
                <Grid item xs={12} key={index}>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField
                      fullWidth
                      label="Earning Label"
                      value={earning.label}
                      onChange={(e) => handleAdditionalEarningChange(index, 'label', e.target.value)}
                      size="small"
                      placeholder="e.g., Conveyance Allowance, Medical Reimbursement, Bonus"
                    />
                    <TextField
                      fullWidth
                      type="number"
                      label="Amount"
                      value={earning.amount}
                      onChange={(e) => handleAdditionalEarningChange(index, 'amount', e.target.value)}
                      size="small"
                      InputProps={{
                        startAdornment: <Typography sx={{ mr: 1 }}>₹</Typography>
                      }}
                    />
                    <IconButton
                      onClick={() => handleRemoveAdditionalEarning(index)}
                      color="error"
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Grid>
              ))}

              {/* Add Additional Earning + Expense Add-on Buttons */}
              <Grid item xs={12} sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleAddAdditionalEarning}
                  sx={{ textTransform: 'none' }}
                >
                  Add Additional Earning
                </Button>

                {approvedExpenses.length > 0 && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => setShowExpenseAddons(!showExpenseAddons)}
                    sx={{
                      textTransform: 'none',
                      color: '#7c2d12',
                      borderColor: '#7c2d12',
                      '&:hover': { borderColor: '#7c2d12', backgroundColor: '#fff7ed' }
                    }}
                  >
                    Expense Add-on ({approvedExpenses.length})
                    {selectedExpenseIds.size > 0 && ` — ${selectedExpenseIds.size} selected`}
                  </Button>
                )}
              </Grid>

              {/* Expense Add-on Table */}
              {approvedExpenses.length > 0 && (
                <>

                  {showExpenseAddons && (
                    <>
                      <div style={{
                        height: approvedExpenses.length * 80 + 56,
                        width: '100%',
                        paddingTop: '5px'
                      }}>
                        <CustomDataGrid
                          onSelectionModelChange={(ids) => {
                            setSelectedExpenseIds(new Set(ids as string[]));
                          }}
                          selectionModel={Array.from(selectedExpenseIds)}
                          checkboxSelection={true}
                          disableSelectionOnClick
                          components={{ Footer: () => null }}
                          columns={[
                            {
                              field: "referenceId",
                              headerName: 'Ref ID',
                              type: "string",
                              flex: 1,
                              renderCell: ({ row }: GridValueGetterParams) => (
                                <Typography sx={{ fontWeight: 500, color: '#6366f1', fontSize: '0.85rem' }}>
                                  {row.referenceId || (row.txnId ? `EXP-${String(row.txnId).padStart(3, '0')}` : '-')}
                                </Typography>
                              )
                            },
                            {
                              field: "paymentDate",
                              headerName: 'Date',
                              type: "string",
                              flex: 0.8,
                              renderCell: ({ row }: GridValueGetterParams) => (
                                <Typography sx={{ fontSize: '0.85rem' }}>
                                  {row.paymentDate ? new Date(row.paymentDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '-'}
                                </Typography>
                              )
                            },
                            {
                              field: "expenseCategory",
                              headerName: 'Category',
                              type: "string",
                              flex: 1,
                              renderCell: ({ row }: GridValueGetterParams) => (
                                <Typography sx={{ fontSize: '0.85rem' }}>{row.expenseCategory?.name || '-'}</Typography>
                              )
                            },
                            {
                              field: "amount",
                              headerName: 'Amount',
                              type: "string",
                              flex: 1,
                              renderCell: ({ row }: GridValueGetterParams) => (
                                <Typography sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                                  {Utils.formatNumber(parseFloat(row.amount).toFixed(2))}
                                </Typography>
                              )
                            },
                          ]}
                          rows={approvedExpenses}
                          getRowId={(row) => row._id}
                          rowHeight={80}
                          sx={{
                            '& .MuiDataGrid-cell': { whiteSpace: 'normal !important', wordWrap: 'break-word', overflow: 'visible !important', textOverflow: 'unset !important', lineHeight: 'normal', padding: '8px', display: 'flex', alignItems: 'flex-start' },
                            '& .MuiDataGrid-cellContent': { whiteSpace: 'normal', overflow: 'visible', textOverflow: 'unset' }
                          }}
                        />
                      </div>
                      {selectedExpenseIds.size > 0 && (
                        <Grid item xs={12}>
                          <Alert severity="info" sx={{ py: 0.5 }}>
                            <Typography fontSize={12}>
                              <strong>{selectedExpenseIds.size} expense(s)</strong> selected — Total: <strong>₹{
                                approvedExpenses
                                  .filter(e => selectedExpenseIds.has(e._id))
                                  .reduce((sum, e) => sum + (e.settleAmount ?? e.amount ?? 0), 0)
                                  .toLocaleString('en-IN')
                              }</strong>
                              {approvedExpenses.filter(e => selectedExpenseIds.has(e._id) && e.expTdsSelected?.apply).length > 0 && (
                                <> | TDS: <strong>₹{
                                  approvedExpenses
                                    .filter(e => selectedExpenseIds.has(e._id) && e.expTdsSelected?.apply)
                                    .reduce((sum, e) => sum + Number(e.expTdsSelected?.totalAmount || 0), 0)
                                    .toLocaleString('en-IN')
                                }</strong></>
                              )}
                              {' '}will be added to salary and marked as PAID
                            </Typography>
                          </Alert>
                        </Grid>
                      )}
                    </>
                  )}
                </>
              )}

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>

              {/* Gross Salary - Read Only */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Gross Monthly Salary"
                  value={calculateGrossSalary()}
                  size="small"
                  InputProps={{
                    readOnly: true,
                    startAdornment: <Typography sx={{ mr: 1 }}>₹</Typography>
                  }}
                  sx={{
                    '& .MuiInputBase-input': {
                      fontWeight: 600,
                      color: '#2e7d32'
                    }
                  }}
                  helperText="Auto-calculated: Basic + HRA + Additional Earnings"
                />
              </Grid>

              {/* Apply Tax Deductions */}
              <Grid item xs={12} md={6} sx={{ display: 'flex', alignItems: 'flex-start', pt: 0.5 }}>
                <FormControlLabel
                  control={<Checkbox checked={applyTax} onChange={(e) => setApplyTax(e.target.checked)} />}
                  label="Apply Tax Deductions"
                  sx={{ m: 0, mt: 0.75 }}
                />
              </Grid>

              {/* Tax Checkboxes - All in one row */}
              {applyTax && (
                <>
                  <Grid item xs={12}>
                    <Box sx={{
                      display: 'flex',
                      gap: 2,
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      '& .MuiFormControlLabel-root': {
                        mr: 0
                      }
                    }}>
                      <FormControlLabel
                        control={<Checkbox checked={applyPF} onChange={(e) => setApplyPF(e.target.checked)} size="small" />}
                        label="PF (12%)"
                      />
                      <FormControlLabel
                        control={<Checkbox checked={applyESI} onChange={(e) => setApplyESI(e.target.checked)} size="small" />}
                        label="ESI (0.75%)"
                      />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <FormControlLabel
                          control={<Checkbox checked={applyPT} onChange={(e) => setApplyPT(e.target.checked)} size="small" />}
                          label="Professional Tax"
                          sx={{ m: 0 }}
                        />
                        <Tooltip
                          title={
                            <Box sx={{ p: 1 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                Professional Tax (PT) - Maharashtra Slabs
                              </Typography>
                              <Typography variant="body2" sx={{ mb: 0.5 }}>
                                • Gross ≤ ₹7,500 = ₹0
                              </Typography>
                              <Typography variant="body2" sx={{ mb: 0.5 }}>
                                • Gross ₹7,501 - ₹10,000 = ₹175/month
                              </Typography>
                              <Typography variant="body2" sx={{ mb: 1 }}>
                                • Gross &gt; ₹10,000 = ₹200/month
                              </Typography>
                              <Typography variant="caption" sx={{ fontStyle: 'italic' }}>
                                Note: PT is a state-specific tax. Maximum ₹2,500/year as per law.
                              </Typography>
                            </Box>
                          }
                          placement="top"
                          arrow
                        >
                          <IconButton size="small" sx={{ p: 0.5 }}>
                            <InfoOutlinedIcon fontSize="small" sx={{ color: '#1976d2' }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <FormControlLabel
                        control={<Checkbox checked={applyTDS} onChange={(e) => setApplyTDS(e.target.checked)} size="small" />}
                        label="TDS"
                      />
                    </Box>
                  </Grid>

                  {applyTDS && (
                    <Grid item xs={12}>
                      <CustomSelect
                        name="tdsRate"
                        id="tdsRate"
                        label="TDS Rate"
                        size="small"
                        value={tdsSelected._id}
                        onChange={handleTdsChange}
                        options={tdsOptionsWithNoTds}
                      />
                    </Grid>
                  )}
                </>
              )}

              {/* Bank Account */}
              <Grid item xs={12} md={6} sx={{ mt: 1 }}>
                <CustomSelect
                  name="fromBankAccount"
                  label="From Bank Account"
                  id="fromBankAccount"
                  size="small"
                  fullWidth
                  options={fromBankAccountOptions}
                  value={selectedFromBankAccount}
                  onChange={(e: any) => setSelectedFromBankAccount(e.target.value)}
                />
              </Grid>

              {/* Payment Status */}
              <Grid item xs={12} md={6} sx={{ mt: 1 }}>
                <TextField
                  select
                  fullWidth
                  label="Payment Status"
                  value={paymentStatus}
                  onChange={(e) => setPaymentStatus(e.target.value)}
                  size="small"
                >
                  <MenuItem value="PROCESSED" disabled={paymentMode === 'CASH'}>
                    Processed {paymentMode === 'CASH' && '(Not available for Cash)'}
                  </MenuItem>
                  <MenuItem value="PAID">Paid</MenuItem>
                </TextField>
              </Grid>

              {/* Payment Mode Radio Group */}
              <Grid item xs={12}>
                <RadioGroup
                  row
                  aria-labelledby="payment-mode-label"
                  value={paymentMode}
                  onChange={(e) => setPaymentMode(e.target.value)}
                  sx={{
                    '& .MuiFormControlLabel-root': {
                      mr: 2,
                      ml: 0,
                      '& .MuiRadio-root': {
                        p: 0.5,
                        mr: 0.5,
                        color: '#6366f1',
                        '&.Mui-checked': {
                          color: '#6366f1',
                        }
                      },
                      '& .MuiTypography-root': {
                        fontSize: '0.875rem'
                      }
                    }
                  }}
                >
                  <FormControlLabel value="CASH" control={<Radio size="small" />} label="Cash" />
                  <FormControlLabel value="NEFT" control={<Radio size="small" />} label="NEFT" />
                  <FormControlLabel value="RTGS" control={<Radio size="small" />} label="RTGS" />
                  <FormControlLabel value="UPI" control={<Radio size="small" />} label="UPI" />
                  <FormControlLabel value="CHEQUE" control={<Radio size="small" />} label="Cheque" />
                  <FormControlLabel value="NETBANKING" control={<Radio size="small" />} label="Net Banking" />
                </RadioGroup>
              </Grid>

              {/* Transaction Reference */}
              {paymentMode !== 'CASH' && (
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label={paymentMode === 'CHEQUE' ? "Cheque Number" :
                      paymentMode === 'UPI' ? "UPI ID" :
                        "Transaction Ref"}
                    value={transactionRef}
                    onChange={(e) => setTransactionRef(e.target.value)}
                  />
                </Grid>
              )}


              {/* Remarks */}
              <Grid item xs={12}>
                <Controller
                  name="remarks"
                  control={methods.control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Remarks (Optional)"
                      multiline
                      rows={2}
                      size="small"
                    />
                  )}
                />
              </Grid>

              {/* Salary Calculation Preview */}
              {/* Always show Total Payable when expense selected */}
              {expenseAddOnTotal > 0 && (
                <Grid item xs={12}>
                  <Box sx={{ p: 1.5, backgroundColor: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: '6px' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ color: '#065f46' }}>Net Salary: <strong>₹{(calculateGrossSalary()).toLocaleString('en-IN')}</strong></Typography>
                      <Typography variant="body2" sx={{ color: '#92400e' }}>Expense Add-on: <strong>₹{expenseAddOnTotal.toLocaleString('en-IN')}</strong></Typography>
                      <Typography variant="body1" sx={{ fontWeight: 700, color: '#059669', fontSize: '1.1rem' }}>
                        Total Payable: ₹{(calculateGrossSalary() + expenseAddOnTotal).toLocaleString('en-IN')}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              )}

              {salaryCalculation && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="subtitle2">Salary Breakdown</Typography>
                    <Typography variant="body2">Gross: ₹{salaryCalculation.summary.grossSalary.toLocaleString('en-IN')}</Typography>
                    <Typography variant="body2">Deductions: ₹{salaryCalculation.deductions.totalDeductions.toLocaleString('en-IN')}</Typography>
                    <Typography variant="body1" fontWeight="bold">Net Salary: ₹{salaryCalculation.summary.netSalary.toLocaleString('en-IN')}</Typography>
                    {expenseAddOnTotal > 0 && (
                      <>
                        <Typography variant="body2" sx={{ color: '#92400e', mt: 0.5 }}>Expense Add-on: ₹{expenseAddOnTotal.toLocaleString('en-IN')}</Typography>
                        <Typography variant="body1" fontWeight="bold" sx={{ color: '#059669' }}>
                          Total Payable to Employee: ₹{(salaryCalculation.summary.netSalary + expenseAddOnTotal).toLocaleString('en-IN')}
                        </Typography>
                      </>
                    )}
                  </Alert>
                </Grid>
              )}
            </Grid>
          </DialogContent>

          <DialogActions sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: '#fff',
            borderTop: '1px solid #c1c5cb',
            boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.1)',
            padding: '12px 24px',
            margin: 0,
            zIndex: 1000
          }}>
            <Box sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2,
              width: '100%'
            }}>
              <Button
                variant='outlined'
                onClick={handleClose}
                sx={{
                  minWidth: '100px',
                  height: '40px'
                }}
              >
                Cancel
              </Button>

              <LoadingButton
                type="button"
                onClick={methods.handleSubmit(onSubmit)}
                variant="contained"
                loading={formSubmitted || status === ApiStatus.LOADING}
                sx={{
                  minWidth: '180px',
                  height: '40px'
                }}
              >
                Process Salary
              </LoadingButton>
            </Box>
          </DialogActions>
        </Box>
      </FormProvider>
    </div>
  );
};

export default DialogProcessSalaryForm;
